
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, BarChart3, Trophy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

interface FeaturedChallengeSectionProps {
  title: string;
  description: string;
  imageUrl: string;
  linkUrl: string;
  buttonText: string;
}

export const FeaturedChallengeSection = ({
  title,
  description,
  imageUrl,
  linkUrl,
  buttonText
}: FeaturedChallengeSectionProps) => {
  const { t, language } = useLanguage();
  
  // Handler to scroll to top when navigating to challenge page
  const handleNavigateToChallenge = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  
  // Status of the challenge - this could be dynamically determined
  const isCompleted = true;
  
  // Translations for labels
  const cashPrizeLabel = language === 'en' ? 'Cash Prize' : 'Cash Prize';
  const viewsText = language === 'en' ? '0.75€ per 1000 views' : '0.75€ par 1000 vues';
  const statsLabel = language === 'en' ? 'Statistics' : 'Statistiques';
  const completedLabel = language === 'en' ? 'Completed' : 'Terminé';
  const viewsStatsText = language === 'en' ? 'views across 87 creations' : 'vues des 87 créations';
  
  return (
    <section className="w-full py-10 px-4 bg-gradient-to-r from-primary/5 to-primary/10 relative overflow-hidden mb-8">
      <div className="absolute inset-0 opacity-5 bg-cover bg-center mix-blend-overlay" style={{ backgroundImage: `url(${imageUrl})` }}></div>
      
      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Content */}
          <div className="lg:col-span-3 space-y-4">
            <Badge variant="secondary" className="mb-2 inline-flex items-center gap-1 px-2.5 py-0.5">
              {language === 'en' ? 'Creative Challenge' : 'Challenge créatif'}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold">{title}</h2>
            <p className="text-muted-foreground text-lg">
              {description}
            </p>
            
            {/* Challenge Info Cards - Updated to match L'amourCestSurcoté style exactly */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
              {/* Cash Prize Card */}
              <div className={`bg-white dark:bg-card rounded-xl shadow-md border ${isCompleted ? 'border-green-300 dark:border-green-800' : 'border-border'} overflow-hidden transition-all duration-300 hover:shadow-lg`}>
                <div className="flex items-center p-4 md:p-5">
                  <div className={`${isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-primary/10'} p-2.5 rounded-full mr-3.5 flex items-center justify-center`}>
                    {isCompleted ? (
                      <Trophy className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <DollarSign className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">{cashPrizeLabel}</p>
                      {isCompleted && (
                        <span className="inline-flex items-center text-[10px] text-green-600 dark:text-green-400 font-medium">
                          <Check className="h-3 w-3 mr-0.5" strokeWidth={3} />
                          {completedLabel}
                        </span>
                      )}
                    </div>
                    <p className="font-bold text-xl md:text-2xl">4000€</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{viewsText}</p>
                  </div>
                </div>
              </div>
              
              {/* Stats Card */}
              <div className="bg-white dark:bg-card rounded-xl shadow-md border border-border overflow-hidden transition-all duration-300 hover:shadow-lg">
                <div className="flex items-center p-4 md:p-5">
                  <div className="bg-primary/10 p-2.5 rounded-full mr-3.5 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">{statsLabel}</p>
                    <div>
                      <p className="font-bold text-xl md:text-2xl">5,6M</p>
                      <p className="text-xs text-muted-foreground">{viewsStatsText}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="pt-4 flex justify-center sm:justify-start">
              <Button asChild variant="gradient" size="lg" className="group">
                <Link to={linkUrl} className="flex items-center" onClick={handleNavigateToChallenge}>
                  {buttonText}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Image - Movie Poster */}
          <div className="lg:col-span-2 relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-xl blur-md opacity-30"></div>
            <motion.div
              initial={{ scale: 0.95, opacity: 0.8 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src={imageUrl} 
                alt={title}
                className="w-full h-auto rounded-lg shadow-2xl border border-white/10 object-cover"
                style={{ aspectRatio: "2/3" }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
